import React, { useEffect, useState } from 'react';
import api from '../services/api';

const AdminEnrollments = () => {
  const [courses, setCourses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [packageEnrollments, setPackageEnrollments] = useState([]);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(false);
  const [grantIdentifier, setGrantIdentifier] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [message, setMessage] = useState('');

  const showMessage = (text, duration = 4000) => {
    setMessage(text);
    setTimeout(() => setMessage(''), duration);
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [coursesRes, packagesRes] = await Promise.all([
          api.get('/api/courses'),
          api.get('/api/question-packages/admin')
        ]);
        setCourses(coursesRes.data || []);
        setPackages(packagesRes.data || []);
      } catch (error) {
        console.error('Failed to load enrollment options:', error);
        setMessage('❌ Failed to load courses/packages');
      }
    };
    loadOptions();
  }, []);

  const loadCourseEnrollments = async (courseId) => {
    if (!courseId) return;
    setLoadingCourse(true);
    try {
      const res = await api.get(`/api/courses/${courseId}/enrollments`);
      setCourseEnrollments(res.data || []);
    } catch (error) {
      console.error('Failed to load course enrollments:', error);
      showMessage('❌ Failed to load course enrollments');
    } finally {
      setLoadingCourse(false);
    }
  };

  const loadPackageEnrollments = async (packageId) => {
    if (!packageId) return;
    setLoadingPackage(true);
    try {
      const res = await api.get(`/api/question-packages/${packageId}/enrollments`);
      setPackageEnrollments(res.data || []);
    } catch (error) {
      console.error('Failed to load package enrollments:', error);
      showMessage('❌ Failed to load package enrollments');
    } finally {
      setLoadingPackage(false);
    }
  };

  const handleGrantCourseAccess = async () => {
    if (!selectedCourseId) return;
    const raw = grantIdentifier.trim();
    if (!raw) {
      showMessage('Enter a student email or user ID to grant access.');
      return;
    }
    const body = /^\d+$/.test(raw)
      ? { userId: parseInt(raw, 10) }
      : { identifier: raw };
    setGrantLoading(true);
    try {
      const res = await api.post(`/api/courses/${selectedCourseId}/grant-access`, body);
      showMessage(res.data?.message || '✅ Access granted');
      setGrantIdentifier('');
      loadCourseEnrollments(selectedCourseId);
    } catch (error) {
      const errMsg = error.response?.data?.error
        || `Could not grant access (${error.response?.status || 'error'})`;
      showMessage(`❌ ${errMsg}`, 6000);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeCourseAccess = async (enrollmentId) => {
    if (!selectedCourseId || !enrollmentId) return;
    if (!window.confirm('Remove this student\'s access to this course?')) return;
    try {
      await api.delete(`/api/courses/${selectedCourseId}/enrollments/${enrollmentId}`);
      showMessage('✅ Access revoked');
      loadCourseEnrollments(selectedCourseId);
    } catch (error) {
      const errMsg = error.response?.data?.error || 'Failed to revoke access';
      showMessage(`❌ ${errMsg}`, 6000);
    }
  };

  const selectedCourse = courses.find(c => String(c.id) === String(selectedCourseId));

  return (
    <div className="section">
      <h3>📋 Enrollments</h3>
      {message && <div style={{ marginBottom: '12px' }}>{message}</div>}

      <div style={{ display: 'grid', gap: '20px' }}>
        <div>
          <h4>Courses</h4>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={selectedCourseId}
              onChange={(e) => {
                setSelectedCourseId(e.target.value);
                setGrantIdentifier('');
                loadCourseEnrollments(e.target.value);
              }}
              style={{ padding: '10px', minWidth: '260px' }}
            >
              <option value="">Select a course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {selectedCourseId && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '14px', color: '#555' }}>
                Grant the same access as a purchase, without payment. Enter numeric user ID (Users tab), or the student&apos;s email or username.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Email or user ID"
                  value={grantIdentifier}
                  onChange={(e) => setGrantIdentifier(e.target.value)}
                  disabled={grantLoading}
                  style={{ padding: '10px', minWidth: '240px', flex: '1' }}
                />
                <button
                  type="button"
                  onClick={handleGrantCourseAccess}
                  disabled={grantLoading}
                  style={{ padding: '10px 16px', cursor: grantLoading ? 'wait' : 'pointer' }}
                >
                  {grantLoading ? 'Granting…' : 'Grant access'}
                </button>
              </div>
            </div>
          )}

          {loadingCourse ? (
            <div style={{ marginTop: '10px' }}>Loading enrollments...</div>
          ) : (
            <div style={{ marginTop: '10px' }}>
              {selectedCourseId && (
                <h5 style={{ margin: '0 0 8px' }}>
                  Students enrolled{selectedCourse ? ` in "${selectedCourse.title}"` : ''} ({courseEnrollments.length})
                </h5>
              )}
              {!selectedCourseId ? (
                <div>Select a course to view or manage enrollments</div>
              ) : courseEnrollments.length === 0 ? (
                <div>No enrollments yet. Grant access above or students purchase in the app.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th align="left">User</th>
                      <th align="left">Email</th>
                      <th align="left">Status</th>
                      <th align="left">Enrolled At</th>
                      <th align="left"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseEnrollments.map((enroll) => (
                      <tr key={enroll.enrollmentId}>
                        <td>{enroll.user?.username}</td>
                        <td>{enroll.user?.email}</td>
                        <td>{enroll.status}</td>
                        <td>{enroll.enrolledAt ? new Date(enroll.enrolledAt).toLocaleString() : '-'}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleRevokeCourseAccess(enroll.enrollmentId)}
                            style={{ padding: '4px 10px', cursor: 'pointer' }}
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div>
          <h4>Question Packages</h4>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={selectedPackageId}
              onChange={(e) => {
                setSelectedPackageId(e.target.value);
                loadPackageEnrollments(e.target.value);
              }}
              style={{ padding: '10px', minWidth: '260px' }}
            >
              <option value="">Select a package</option>
              {packages.map(pkg => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </option>
              ))}
            </select>
          </div>
          {loadingPackage ? (
            <div style={{ marginTop: '10px' }}>Loading enrollments...</div>
          ) : (
            <div style={{ marginTop: '10px' }}>
              {packageEnrollments.length === 0 ? (
                <div>No enrollments found</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th align="left">User</th>
                      <th align="left">Email</th>
                      <th align="left">Status</th>
                      <th align="left">Enrolled At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packageEnrollments.map((enroll) => (
                      <tr key={enroll.enrollmentId}>
                        <td>{enroll.user?.username}</td>
                        <td>{enroll.user?.email}</td>
                        <td>{enroll.status}</td>
                        <td>{enroll.enrolledAt ? new Date(enroll.enrolledAt).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <p style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
            To grant or revoke package access, use the Question Packages admin tab.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminEnrollments;
